import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Header from './components/Header/Header';
import Hero from './components/Hero/Hero';
import Comparison from './components/Comparison/Comparison';
import LoginModal from './components/Auth/LoginModal';
import ForgotPasswordModal from './components/Auth/ForgotPasswordModal';
import ResetPasswordModal from './components/Auth/ResetPasswordModal';
import Workspace from './components/Workspace/Workspace';
import FocusSection from './components/Focus/FocusSection';
import WhySection from './components/Why/WhySection';
import DataBlocksSection from './components/DataBlocks/DataBlocksSection';
import Footer from './components/Footer/Footer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthSessionHandler from './components/Auth/AuthSessionHandler';
import './App.css';
import DemoPage from './components/Demo/DemoPage';
import ResearchProjectPage from './components/Workspace/ResearchProjectPage';
import PrivateCirclePage from './components/PrivateCircle/PrivateCirclePage';
import PrivateCircleCommunityPage from './components/PrivateCircle/PrivateCircleCommunityPage';
import CommunityEntryRedirect from './components/PrivateCircle/CommunityEntryRedirect';
import CommunityApplicationPendingPage from './components/PrivateCircle/CommunityApplicationPendingPage';
import CommunityAccessGate from './components/PrivateCircle/CommunityAccessGate';
import { WaitlistBar } from './components/Waitlist/WaitlistBar';
import { WaitlistModal } from './components/Waitlist/WaitlistModal';
import { COMMUNITY_ENABLED } from './config/featureFlags';
import { parseHashQueryParams } from './utils/hashQueryParams';

// Компонент для защищенных маршрутов
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Загрузка...</div>;
  }
  
  return user ? <>{children}</> : <Navigate to="/" replace />;
};

// Глобальная обёртка внутри Router — слушает PASSWORD_RECOVERY на любой странице
const AppContent: React.FC = () => {
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [linkError, setLinkError] = useState('');

  // Проверяем ошибку в URL-хэше (например, истёкшая ссылка сброса пароля)
  useEffect(() => {
    const params = parseHashQueryParams(window.location.hash);
    if (params['error']) {
      const code = params['error_code'] || '';
      if (code === 'otp_expired' || code === 'otp_disabled') {
        setLinkError('Ссылка для сброса пароля истекла или уже была использована. Запросите новую.');
      } else {
        setLinkError(params['error_description'] || 'Недействительная ссылка. Попробуйте снова.');
      }
      setIsForgotPasswordOpen(true);
      // Очищаем хэш, чтобы ошибка не повторялась при перезагрузке
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const params = parseHashQueryParams(window.location.hash);
    if (params['reset_token']) {
      setIsResetPasswordOpen(true);
    }
  }, []);

  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/demo" element={<DemoPage />} />
        {COMMUNITY_ENABLED ? (
          <>
            <Route path="/private-circle" element={<PrivateCirclePage />} />
            <Route path="/private-circle/enter" element={<CommunityEntryRedirect />} />
            <Route path="/private-circle/application/pending" element={<CommunityApplicationPendingPage />} />
            <Route
              path="/private-circle/community"
              element={(
                <CommunityAccessGate>
                  <PrivateCircleCommunityPage />
                </CommunityAccessGate>
              )}
            />
          </>
        ) : (
          <>
            <Route path="/private-circle" element={<Navigate to="/" replace />} />
            <Route path="/private-circle/community" element={<Navigate to="/" replace />} />
          </>
        )}
        <Route
          path="/workspace"
          element={
            <ProtectedRoute>
              <Workspace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:id"
          element={
            <ProtectedRoute>
              <ResearchProjectPage />
            </ProtectedRoute>
          }
        />
      </Routes>

      <ResetPasswordModal
        isOpen={isResetPasswordOpen}
        onClose={() => setIsResetPasswordOpen(false)}
      />

      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => { setIsForgotPasswordOpen(false); setLinkError(''); }}
        onBackToLogin={() => setIsForgotPasswordOpen(false)}
        initialError={linkError}
      />

      <AuthSessionHandler />
    </>
  );
};

// Компонент для главной страницы
const HomePage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [loginRedirect, setLoginRedirect] = useState<string | null>(null);
  const { signIn, signUp, user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogin = () => {
    setLoginRedirect(null);
    setIsModalOpen(true);
  };

  const handlePrivateCircleClick = () => {
    if (user) {
      navigate('/private-circle/enter');
      return;
    }
    setLoginRedirect('/private-circle/enter');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setLoginRedirect(null);
  };

  const handleLoginSubmit = async (email: string, password: string): Promise<boolean> => {
    try {
      await signIn(email, password);
      setIsModalOpen(false);
      if (loginRedirect) {
        navigate(loginRedirect);
      }
      setLoginRedirect(null);
      return true;
    } catch (error) {
      console.error('Ошибка входа:', error);
      return false;
    }
  };

  const handleRegisterSubmit = async (name: string, email: string, password: string) => {
    try {
      const result = await signUp(name, email, password);
      // После регистрации остаёмся на главной: закрываем модалку, без редиректа в ЛК/сообщество
      setIsModalOpen(false);
      setLoginRedirect(null);
      return result;
    } catch (error) {
      console.error('Ошибка регистрации:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await signOut();
    } catch (error) {
      console.error('Ошибка выхода:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleGoToWorkspace = () => {
    navigate('/workspace');
  };

  return (
    <div className="App">
      {/* Основной контент главной страницы */}
      {COMMUNITY_ENABLED && (
        <WaitlistBar
          onOpen={() => setIsWaitlistOpen(true)}
          onPrivateCircleClick={handlePrivateCircleClick}
        />
      )}
      <Header 
        onLoginClick={handleLogin}
        user={user}
        onLogout={handleLogout}
        onDashboardClick={handleGoToWorkspace}
        onPrivateCircleClick={handlePrivateCircleClick}
        isLoggingOut={isLoggingOut}
      />
      <Hero user={user} />
      <Comparison />
      <FocusSection />
      <WhySection />
      <DataBlocksSection />
      <Footer />

      <LoginModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onLogin={handleLoginSubmit}
        onRegister={handleRegisterSubmit}
        onForgotPassword={() => setIsForgotPasswordOpen(true)}
        redirectPath={loginRedirect ?? '/'}
      />

      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
        onBackToLogin={() => { setIsForgotPasswordOpen(false); setIsModalOpen(true); }}
      />

      {COMMUNITY_ENABLED && (
        <WaitlistModal isOpen={isWaitlistOpen} onClose={() => setIsWaitlistOpen(false)} />
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
